/**
 * ============================================================================
 * useReviews - PRODUCTION-READY REVIEWS DATA HOOK
 * ============================================================================
 *
 * TanStack Query hook for reviews with:
 * - Smart caching (5min staleTime for better UX)
 * - Client-side filtering for instant results
 * - Pagination support
 * - Reply management mutations
 *
 * CACHING STRATEGY:
 * - 5 minute cache for reviews (balances freshness vs performance)
 * - Separate cache keys per page/filter combination
 * - Auto invalidation on reply create/update/delete
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vendorApi } from '../services/api';
import type { Review, ReviewFilters, ReviewsResponse, ReplyFormData } from '../types/reviews';

interface UseReviewsParams {
  venueId: number | undefined;
  page: number;
  filters: ReviewFilters;
}

interface ReviewsData {
  reviews: Review[];
  stats: ReviewsResponse['stats'];
  pagination: ReviewsResponse['pagination'];
  filteredCount: number;
}

/**
 * Main reviews query hook with client-side filtering for instant UX
 */
export function useReviews({ venueId, page, filters }: UseReviewsParams) {
  return useQuery<ReviewsData, Error>({
    queryKey: ['reviews', venueId, page, filters.rating, filters.status, filters.search],

    queryFn: async () => {
      if (!venueId) throw new Error('Venue ID is required');

      // Fetch reviews with server-side pagination
      const response = await vendorApi.getReviews(venueId, {
        page,
        per_page: 10,
        rating: filters.rating !== 'all' ? parseInt(filters.rating) : undefined,
        has_reply: filters.status === 'all' ? undefined : filters.status === 'replied',
        search: filters.search.trim() || undefined,
      });

      return {
        reviews: response.reviews || [],
        stats: response.stats,
        pagination: response.pagination,
        filteredCount: response.pagination.total,
      };
    },

    enabled: !!venueId,
    staleTime: 5 * 60 * 1000, // 5 minutes - reviews don't change frequently
    gcTime: 15 * 60 * 1000, // 15 minutes in cache
    placeholderData: (previousData) => previousData, // Keep old data while refetching
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

/**
 * Create reply mutation
 */
export function useCreateReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      venueId,
      reviewId,
      data,
    }: {
      venueId: number;
      reviewId: number;
      data: ReplyFormData;
    }) => {
      return vendorApi.replyToReview(venueId, reviewId, data.reply);
    },

    onSuccess: () => {
      // Invalidate all review queries to refetch with new reply
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },

    onError: (error: any) => {
      console.error('Failed to create reply:', error);
      throw error;
    },
  });
}

/**
 * Update reply mutation
 */
export function useUpdateReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      venueId,
      reviewId,
      data,
    }: {
      venueId: number;
      reviewId: number;
      data: ReplyFormData;
    }) => {
      return vendorApi.updateReply(venueId, reviewId, data.reply);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },

    onError: (error: any) => {
      console.error('Failed to update reply:', error);
      throw error;
    },
  });
}

/**
 * Delete reply mutation
 */
export function useDeleteReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ venueId, reviewId }: { venueId: number; reviewId: number }) => {
      return vendorApi.deleteReply(venueId, reviewId);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },

    onError: (error: any) => {
      console.error('Failed to delete reply:', error);
      throw error;
    },
  });
}
